import { useCallback, useEffect, useState } from 'react'
import { useMatch } from 'react-router'
import { skipToken } from '@reduxjs/toolkit/query'

import type { ModalType } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { selectSavedStateDetailsId } from 'features/RiskManagement/Assessments/States/qraSavedStateSelectors'

import type { IWisDetailsModalTabsProps } from './types'
import { WisDetailsModalAssesments } from './Assessments'
import { WisDetailsModalBody } from './Body'
import { WisDetailsModalScenario } from './Scenario'
import { WisDetailsModalTabs } from './Tabs'

export const WisDetailsModal = ({ onClose }: ModalType) => {
  // to view details on saved states page, where route data is not set
  const savedStateDetailsId = useAppSelector(selectSavedStateDetailsId)

  const systemViewAssessmentId = useMatch(
    // ISavedSateRouteParams
    '/risk-management/assessments/states/:stateId/assessments/:assessmentId?/:type?'
  )

  const lineViewAssessmentId = useMatch(
    '/risk-management/assessments/states/:stateId/assessments/:assessmentId/line/:lineId/:type?'
  )

  const matchCompareIds = useMatch(
    // ICompareViewRouteParams
    '/risk-management/assessments/compare/:state1Id/:state2Id'
  )

  const { stateId, assessmentId } = systemViewAssessmentId?.params ??
    lineViewAssessmentId?.params ?? {
      stateId: savedStateDetailsId?.toString()
    }

  const { state1Id, state2Id } = matchCompareIds?.params ?? {}

  // assessments can also be filtered out from the state.assessments
  const { assessment } = useGetQRAAssessmentQuery(assessmentId ?? skipToken, {
    selectFromResult: (result) => ({
      assessment: result.currentData
    })
  })

  const { state1 } = useGetQRASavedStateQuery(
    state1Id ?? stateId ?? skipToken,
    {
      selectFromResult: (result) => ({
        state1: result.currentData
      })
    }
  )

  const { state2 } = useGetQRASavedStateQuery(state2Id ?? skipToken, {
    selectFromResult: (result) => ({
      state2: result.currentData
    })
  })

  const [currentState, setCurrentState] = useState(state1 || state2)

  useEffect(() => {
    setCurrentState(state1 || state2)
  }, [state1, state2])

  const onChange = useCallback<
    NonNullable<IWisDetailsModalTabsProps['onChange']>
  >((_, b) => {
    setCurrentState(b)
  }, [])

  // TODO: add a loader in case query takes some time to fetch
  if (!currentState) {
    return null
  }

  const title = assessmentId ? 'WIS Run Details' : undefined

  return (
    <WisDetailsModalBody isOpen close={onClose} title={title}>
      <WisDetailsModalTabs
        currentState={currentState}
        state1={state1}
        state2={state2}
        onChange={onChange}
      />
      {assessment ? (
        <WisDetailsModalScenario
          currentState={currentState}
          assessment={assessment}
        />
      ) : (
        <OverlayScrollbar>
          <WisDetailsModalScenario
            currentState={currentState}
            assessment={assessment}
          />
          <WisDetailsModalAssesments currentState={currentState} />
        </OverlayScrollbar>
      )}
    </WisDetailsModalBody>
  )
}

export default WisDetailsModal
